<template>
  <fts-modal
    :append-to-body="appendToBody"
    :width="width"
    :full-screen="fullScreen"
    :escape-event="escapeEvent"
    :backdrop="backdrop"
    :persistent="persistent"
    :header="isHeader"
    :footer="isFooter"
    :append-container-class="appendContainerClass"
    :header-class="getHeaderClass"
    :content-class="getContentClass"
    :footer-class="getFooterClass"
    class="fts-modal-standard"
    @opened="$emit('opened')"
    @close="beforeClose"
    @focus="$emit('focus')"
    @blur="$emit('blur')"
    v-if="showDialog"
  >
    <slot slot="header" name="header">
      <slot name="title">
        <div class="modal-standard-title" v-if="isTitle">
          {{title}}
        </div>
      </slot>

      <slot name="description">
        <div class="modal-standard-description" v-if="isDescription">
          {{description}}
        </div>
      </slot>

      <font-awesome-icon 
        :icon="['fas', 'xmark']"
        fixed-width
        class="modal-standard-header-close"
        :class="{'disabled': disableClose}"
        @click="beforeClose"
        v-if="!disableHeaderClose"
      />
    </slot>

    <slot slot="body" name="body">
    </slot>

    <slot slot="footer" name="footer">
      <div class="modal-standard-footer-l" v-if="isFooterLeft">
        <slot name="footerLeft" />
      </div>
      <div class="modal-standard-footer-r" v-if="isFooterRight">
        <slot name="footerRight" />
      </div>
    </slot>
  </fts-modal>
</template>

<script>
import FTSModal from './FTSModal';

export default {
  components: {
    'fts-modal': FTSModal
  },
  props: {
    appendToBody: Boolean,
    width: String,
    fullScreen: Boolean,
    backdrop: {
      type: Boolean,
      default: true,
    },
    escapeEvent: Boolean,
    persistent: Boolean,
    appendContainerClass: String,
    appendHeaderClass: String,
    appendContentClass: String,
    appendFooterClass: String,
    title: String,
    description: String,
    disableHeaderClose: Boolean,
    disableClose: Boolean
  },
  data: () => ({
    showDialog: false,
    beforeCloseHandler: null
  }),
  methods: {
    show(item) {
      if (item && item.beforeCloseHandler) {
        this.beforeCloseHandler = item.beforeCloseHandler;
      }
      this.showDialog = true;
    },
    beforeClose() {
      if (this.disableClose) return;

      if (this.beforeCloseHandler && typeof this.beforeCloseHandler === 'function') {
        this.beforeCloseHandler();
      } else {
        this.close();
      }
    },
    close() {
      this.showDialog = false;
      this.$emit("closed");
    },
  },
  computed: {
    isTitle() {
      return (
        Boolean(this.title) ||
        Boolean(this.$slots.title) ||
        Boolean(this.$scopedSlots.title)
      );
    },
    isDescription() {
      return (
        Boolean(this.description) ||
        Boolean(this.$slots.description) ||
        Boolean(this.$scopedSlots.description)
      );
    },
    isHeader() {
      return (
        this.isTitle ||
        this.isDescription ||
        Boolean(this.$slots.header) ||
        Boolean(this.$scopedSlots.header)
      );
    },
    isFooterLeft() {
      return Boolean(this.$slots.footerLeft) || Boolean(this.$scopedSlots.footerLeft);
    },
    isFooterRight() {
      return Boolean(this.$slots.footerRight) || Boolean(this.$scopedSlots.footerRight);
    },
    isFooter() {
      return (
        this.isFooterLeft ||
        this.isFooterRight ||
        Boolean(this.$slots.footer) ||
        Boolean(this.$scopedSlots.footer)
      );
    },
    getHeaderClass() {
      let classes = "modal-standard__header";
      if (this.appendHeaderClass)
        classes += ` ${this.appendHeaderClass}`
      return classes;
    },
    getContentClass() {
      let classes = "modal-standard__content";
      if (this.appendContentClass)
        classes += ` ${this.appendContentClass}`
      return classes;
    },
    getFooterClass() {
      let classes = "modal-standard__footer";
      if (this.appendFooterClass)
        classes += ` ${this.appendFooterClass}`
      return classes;
    }
  }
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

.fts-modal-standard {
  .modal-standard__header {
    position: relative;
    padding-top: 2rem;
    padding-left: 2.5rem;
    padding-right: 2.5rem;
    text-align: center;
  }
  .modal-standard__content {
    padding: 1rem;
  }
  .modal-standard__footer {
    display: flex;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 1rem;
  }
  .modal-standard-footer-l {
    @include flex-gap($gap: 0.375rem);
  }
  .modal-standard-footer-r {
    @include flex-gap($gap: 0.375rem);
    justify-content: flex-end;
    flex: 1;
  }
  .modal-standard-title {
    font-weight: 500;
    font-size: 1.5rem;
    line-height: 1.75rem;
    color: var(--fts-text);
    & + .modal-standard-description {
      margin-top: 0.5rem;
    }
  }
  .modal-standard-description {
    font-size: 1rem;
    line-height: 1.5rem;
    color: var(--fts-default);
  }
  .modal-standard-header-close {
    position: absolute;
    top: 1rem;
    right: 1rem;
    color: var(--fts-default);
    font-size: 1.25rem;
    &:hover {
      color: var(--fts-text);
      cursor: pointer;
    }
    &.disabled {
      color: var(--fts-default);
      cursor: not-allowed;
    }
  }
}
</style>